/**
 * @fileoverview The money of a simulated EUA hedge (docs/features/cbam-hedging-scenarios/,
 * HG9, HG12, HG13, I3, I4). Pure, in Decimal; components never multiply.
 *
 * Every instrument has one payoff shape. A leg of `quantity` EUAs, entered at
 * its locked price and closed when the market is at `P`, makes
 * `quantity × (P − lockedPrice)`:
 *
 *   spot     buy EUAs now at S₀, sell at P
 *   forward  agree F now, close out at P (EUAs can't be surrendered for CBAM)
 *   swap     fixed K against a floating price; under one P the float reads as P
 *
 * Basis is zero (HG12): the EUA price at close is taken to be the certificate
 * price. The certificate fee is paid on the certificates and never hedged.
 *
 * Every function returns a `Decimal` (or `null` for unknown, I3); the caller
 * rounds once, for display.
 */

import Decimal from 'decimal.js';
import { FEE_PER_CERTIFICATE } from 'modules/CbamCertificates/config/certificateRules';

export const HEDGE_INSTRUMENTS = Object.freeze(['spot', 'forward', 'swap']);

export const isBlank = (v) => v == null || v === '' || !Number.isFinite(Number(v));
const D = (v) => new Decimal(isBlank(v) ? 0 : v);

/**
 * A leg counts only with a positive quantity and a positive locked price — the
 * same rule the save applies, so a leg on screen is never dropped on save.
 */
export const isLive = (leg) =>
  !isBlank(leg?.quantity) &&
  Number(leg.quantity) > 0 &&
  !isBlank(leg?.lockedPrice) &&
  Number(leg.lockedPrice) > 0;

const liveLegs = (legs) => (legs ?? []).filter(isLive);

/** One leg's gain (positive) or loss at market price `P`. */
export const legPnl = (leg, P) => {
  if (isBlank(P)) return null; // no market price, no P&L (I3) — never a price of 0
  return isLive(leg) ? D(leg.quantity).times(D(P).minus(leg.lockedPrice)) : new Decimal(0);
};

/** The hedges' P&L at `P`, summed; `null` when `P` is unknown. */
export const hedgePnl = (legs, P) =>
  isBlank(P) ? null : liveLegs(legs).reduce((sum, leg) => sum.plus(legPnl(leg, P)), new Decimal(0));

/**
 * The cash a leg needs today: the full price for spot, the collateral share
 * for a forward, nothing for a swap. No interest or carry (HG12).
 */
export const legCashToday = (leg) => {
  if (!isLive(leg)) return new Decimal(0);
  const notional = D(leg.quantity).times(leg.lockedPrice);
  if (leg.instrument === 'spot') return notional;
  if (leg.instrument === 'forward') return notional.times(D(leg.collateralPct)).div(100);
  return new Decimal(0);
};

export const cashToday = (legs) =>
  liveLegs(legs).reduce((sum, leg) => sum.plus(legCashToday(leg)), new Decimal(0));

/** EUAs hedged, over every live leg. */
export const hedgedQuantity = (legs) =>
  liveLegs(legs).reduce((sum, leg) => sum.plus(D(leg.quantity)), new Decimal(0));

/** What `Q` exposed certificates cost at `P`, fees included. `null` when either is unknown. */
export const exposureCost = (Q, P, fee = FEE_PER_CERTIFICATE) =>
  isBlank(Q) || isBlank(P) ? null : D(Q).times(D(P).plus(fee));

/** `cost` less the hedges' P&L at `P`; `null` when either is unknown (I3). */
export const netOf = (cost, legs, P) => {
  const pnl = hedgePnl(legs, P);
  return cost == null || pnl == null ? null : cost.minus(pnl);
};

/** The exposure's cost at `P` less the hedges' P&L at `P`. `null` when the exposure is unknown. */
export const netCost = ({ Q, P, legs, fee = FEE_PER_CERTIFICATE }) =>
  netOf(exposureCost(Q, P, fee), legs, P);

/** Hedged EUAs ÷ exposed certificates; `null` with no (or unknown) exposure. */
export const hedgeRatio = (Q, legs) =>
  isBlank(Q) || Number(Q) <= 0 ? null : hedgedQuantity(legs).div(Q);

/**
 * The payoff across a band of market prices around `spot`:
 * `points` prices from `spot × (1 − band)` to `spot × (1 + band)`, each with the
 * unhedged cost and the net cost. Empty when spot or exposure is unknown.
 *
 * @returns {Array<{ P: Decimal, unhedged: Decimal, hedged: Decimal }>}
 */
export const payoffCurve = ({
  Q,
  spot,
  legs,
  band = 0.5,
  points = 21,
  fee = FEE_PER_CERTIFICATE,
}) => {
  if (isBlank(spot) || Number(spot) <= 0 || isBlank(Q) || points < 2) return [];
  const low = D(spot).times(new Decimal(1).minus(band));
  const step = D(spot)
    .times(band)
    .times(2)
    .div(points - 1);
  return Array.from({ length: points }, (_, i) => {
    const P = low.plus(step.times(i));
    return { P, unhedged: exposureCost(Q, P, fee), hedged: netCost({ Q, P, legs, fee }) };
  });
};

/**
 * How much the net cost moves per €1 of EUA price: the certificates left
 * unhedged. `Q` with no hedges; 0 under a full hedge; `null` when unknown.
 */
export const sensitivity = (Q, legs) => (isBlank(Q) ? null : D(Q).minus(hedgedQuantity(legs)));

/**
 * The EUA price at which hedged and unhedged cost are equal: the hedges'
 * quantity-weighted locked price. Below it the hedges cost more than they save
 * — the upside a hedge gives up. `null` with no live leg.
 */
export const breakEvenPrice = (legs) => {
  const qty = hedgedQuantity(legs);
  if (qty.isZero()) return null;
  return liveLegs(legs)
    .reduce((sum, leg) => sum.plus(D(leg.quantity).times(leg.lockedPrice)), new Decimal(0))
    .div(qty);
};

/** A Decimal to the cent, as a number for display. `null` stays `null`. */
export const toCents = (d) => (d == null ? null : d.toDecimalPlaces(2).toNumber());
